/**
 * Agrupación de las vistas de sesión en pestañas, y la resolución de cuál está activa.
 *
 * Es la navegación que la cabecera de `routes/armazon.tsx` dejaba pendiente: las vistas ya existen
 * —veinte caminos de sesión en la tabla del enrutador— y hasta ahora solo se llegaba a ellas
 * escribiendo la dirección, porque el encabezado únicamente ofrecía los consentimientos y la salida.
 *
 * ## Por qué agrupar, y con qué criterio
 *
 * **Veinte enlaces en fila no son una navegación.** A 320 px envolverían en seis o siete líneas y
 * empujarían el contenido de la vista fuera de la pantalla; y a cualquier ancho obligan a leer los
 * veinte rótulos para encontrar uno. La agrupación es lo que convierte esa lista en algo que se
 * recorre: siete pestañas que caben en dos líneas en el teléfono, y dentro de la activa las vistas de
 * ese dominio.
 *
 * **El criterio es el dominio de uso, y no la jerarquía de la dirección.** Las dos cosas no coinciden
 * y la diferencia se ve mejor en el sensor: su camino es `/perfil/sensor` porque conectar LibreLinkUp
 * es entregar la credencial de una cuenta de un tercero, y eso vive donde vive lo que la persona
 * administra sobre sus propios datos —así lo argumenta la declaración de {@link RUTA_DEL_SENSOR}—.
 * Pero nadie abre esa pantalla pensando en administrar datos: se abre porque las glicemias dejaron de
 * llegar, o para mirar cuántos días le quedan al sensor. Su pestaña es Glucosa. La dirección conserva
 * la razón de propiedad del dato; la pestaña sigue la razón de uso.
 *
 * De ahí los siete grupos, cada uno con la pregunta que responde:
 *
 * | Pestaña           | Responde a                                                    |
 * | ----------------- | ------------------------------------------------------------- |
 * | Inicio            | ¿cómo voy hoy?                                                |
 * | Glucosa           | ¿qué hizo mi glicemia, y por qué?                             |
 * | Alimentación      | ¿qué como, qué comí y qué tengo que comprar?                  |
 * | Entrenamiento     | ¿qué entreno hoy y con cuánta carga?                          |
 * | Cuerpo            | ¿cómo va cambiando mi cuerpo?                                 |
 * | Logros e informes | ¿qué llevo acumulado y qué le muestro a mi médico?            |
 * | Perfil            | ¿qué sabe la plataforma de mí y qué le permito?                |
 *
 * Las decisiones de reparto que no son obvias, con su motivo:
 *
 * - **La correlación va en Glucosa y no en Alimentación**, aunque hable de comidas: lo que se busca
 *   ahí es la variación de la glicemia después de comer, y se llega desde la serie y no desde el plan
 *   semanal.
 * - **Proteína e hidratación van en Alimentación**, con el plan, las comidas, las compras y las
 *   recetas: son las otras dos cosas que se registran al comer y beber. Es el grupo más grande —seis
 *   vistas—, y partirlo en dos pestañas obligaría a recordar en cuál de las dos está el vaso de agua.
 * - **La galería de progreso va en Cuerpo**, junto a la composición: son fotografías del cuerpo a lo
 *   largo del tiempo, la misma pregunta que responden los pliegues y los perímetros.
 * - **Los recordatorios van en Perfil**: son una preferencia de la cuenta, no una vista de datos.
 *
 * ## Lo que este módulo no hace
 *
 * No dibuja: eso es `componentes/navegacion.tsx`. Aquí solo hay datos y una función pura, y la
 * separación es la que hace comprobable la parte que importa. Que ninguna ruta quede sin pestaña se
 * verifica recorriendo estas constantes contra las de `lib/rutas.ts` en `navegacion.test.ts`, sin
 * montar un navegador; y agregar mañana una ruta al enrutador sin sumarla a un grupo pone esa prueba
 * en rojo, que es exactamente el olvido que una lista escrita a mano invita a cometer.
 *
 * Los caminos **no se escriben aquí**: se reutilizan de `lib/rutas.ts`, que es su única declaración.
 * Una pestaña con el camino copiado sería la tercera copia de una cadena que ya se escribe en la tabla
 * del enrutador y en los enlaces que llegan a la vista.
 */

import {
  RUTA_DE_AGUA,
  RUTA_DE_ALIMENTACION,
  RUTA_DE_CARDIO,
  RUTA_DE_COMIDAS,
  RUTA_DE_COMPOSICION,
  RUTA_DE_COMPRAS,
  RUTA_DE_CONSENTIMIENTOS,
  RUTA_DE_CORRELACION,
  RUTA_DE_DERECHOS_DE_DATOS,
  RUTA_DE_FUERZA,
  RUTA_DE_GLICEMIAS,
  RUTA_DE_INGRESO,
  RUTA_DE_INICIO,
  RUTA_DE_LOGROS,
  RUTA_DE_PERFIL,
  RUTA_DE_PROGRESO,
  RUTA_DE_PROTEINA,
  RUTA_DE_RECETAS,
  RUTA_DE_RECORDATORIOS,
  RUTA_DE_RECUPERACION,
  RUTA_DE_REGISTRO,
  RUTA_DE_RESTABLECIMIENTO,
  RUTA_DEL_INFORME,
  RUTA_DEL_SENSOR,
} from './rutas';

/** Una vista de sesión tal como se ofrece dentro de su pestaña. */
export interface VistaDeNavegacion {
  /** Camino de la vista, reutilizado de `lib/rutas.ts` y nunca escrito aquí. */
  readonly camino: string;

  /**
   * Rótulo visible del enlace, en español y corto.
   *
   * Es texto y no un icono: un icono solo no dice a dónde lleva, y el criterio 26.6 exige que el
   * nombre accesible sea el mismo que se ve. Corto porque es lo que permite que la fila envuelva en
   * lugar de arrastrar la página a 320 px (criterio 26.4).
   */
  readonly etiqueta: string;
}

/**
 * Un grupo de vistas del mismo dominio de uso: una pestaña.
 *
 * `vistas` es una tupla no vacía a propósito. Un grupo sin vistas no es un grupo —sería una pestaña
 * que no lleva a ninguna parte—, y declararlo así deja que la primera vista sea el destino de la
 * pestaña sin una comprobación de ausencia que nunca se cumpliría.
 */
export interface GrupoDeNavegacion {
  /** Identificador estable del grupo, para la clave del listado y para los mensajes de las pruebas. */
  readonly id: string;

  /** Rótulo visible de la pestaña. */
  readonly etiqueta: string;

  /** Las vistas del grupo. La primera es el destino de la pestaña: su portada. */
  readonly vistas: readonly [VistaDeNavegacion, ...VistaDeNavegacion[]];
}

/**
 * Los siete grupos, en el orden en que se dibujan.
 *
 * El orden es el del recorrido de un día: se entra al resumen, se mira la glicemia, se registra lo que
 * se comió, se entrena, y las tres últimas se abren de vez en cuando. No es alfabético a propósito:
 * alfabético pondría Alimentación antes que Glucosa y Perfil en medio, y la pestaña que más se pulsa
 * quedaría en cualquier parte.
 */
export const GRUPOS_DE_NAVEGACION: readonly GrupoDeNavegacion[] = [
  {
    id: 'inicio',
    etiqueta: 'Inicio',
    vistas: [{ camino: RUTA_DE_INICIO, etiqueta: 'Resumen del día' }],
  },
  {
    id: 'glucosa',
    etiqueta: 'Glucosa',
    vistas: [
      { camino: RUTA_DE_GLICEMIAS, etiqueta: 'Glicemias' },
      { camino: RUTA_DE_CORRELACION, etiqueta: 'Comidas y glicemias' },
      { camino: RUTA_DEL_SENSOR, etiqueta: 'Sensor' },
    ],
  },
  {
    id: 'alimentacion',
    etiqueta: 'Alimentación',
    vistas: [
      { camino: RUTA_DE_ALIMENTACION, etiqueta: 'Plan semanal' },
      { camino: RUTA_DE_COMIDAS, etiqueta: 'Comidas del día' },
      { camino: RUTA_DE_COMPRAS, etiqueta: 'Compras' },
      { camino: RUTA_DE_RECETAS, etiqueta: 'Recetas' },
      { camino: RUTA_DE_PROTEINA, etiqueta: 'Proteína' },
      { camino: RUTA_DE_AGUA, etiqueta: 'Hidratación' },
    ],
  },
  {
    id: 'entrenamiento',
    etiqueta: 'Entrenamiento',
    vistas: [
      { camino: RUTA_DE_FUERZA, etiqueta: 'Fuerza' },
      { camino: RUTA_DE_CARDIO, etiqueta: 'Cardio en Zona 2' },
    ],
  },
  {
    id: 'cuerpo',
    etiqueta: 'Cuerpo',
    vistas: [
      { camino: RUTA_DE_COMPOSICION, etiqueta: 'Composición corporal' },
      { camino: RUTA_DE_PROGRESO, etiqueta: 'Galería de progreso' },
    ],
  },
  {
    id: 'logros',
    etiqueta: 'Logros e informes',
    vistas: [
      { camino: RUTA_DE_LOGROS, etiqueta: 'Logros' },
      { camino: RUTA_DEL_INFORME, etiqueta: 'Informe integral' },
    ],
  },
  {
    id: 'perfil',
    etiqueta: 'Perfil',
    vistas: [
      { camino: RUTA_DE_PERFIL, etiqueta: 'Datos y preferencias' },
      { camino: RUTA_DE_CONSENTIMIENTOS, etiqueta: 'Consentimientos' },
      { camino: RUTA_DE_RECORDATORIOS, etiqueta: 'Recordatorios' },
      { camino: RUTA_DE_DERECHOS_DE_DATOS, etiqueta: 'Tus datos' },
    ],
  },
] as const;

/**
 * Las rutas que **no** llevan pestaña, cada una con su motivo.
 *
 * Son las cuatro del flujo de autenticación, y quedan fuera por la misma razón: la navegación solo se
 * dibuja con sesión, así que en las cuatro pantallas donde estas rutas se usan no hay barra en la que
 * ponerlas. Ofrecerlas dentro de la sesión sería peor que inútil: un enlace a «Crear cuenta» junto a
 * las glicemias de quien ya entró.
 *
 * La lista existe para que la prueba de huérfanas pueda ser exhaustiva. Recorre las constantes de
 * `lib/rutas.ts` y exige que cada una esté en un grupo o esté aquí con su motivo escrito, de modo que
 * una ruta nueva sin pestaña rompa la corrida en lugar de quedarse sin manera de llegar a ella.
 */
export const CAMINOS_FUERA_DE_LA_NAVEGACION: Readonly<Record<string, string>> = {
  [RUTA_DE_INGRESO]:
    'la navegación solo se dibuja con sesión, y esta es la pantalla de quien todavía no la tiene',
  [RUTA_DE_REGISTRO]:
    'crear una cuenta es lo que se hace antes de la sesión; dentro de ella el enlace no lleva a ninguna parte útil',
  [RUTA_DE_RECUPERACION]:
    'se llega desde el ingreso cuando la contraseña no entra, y quien está dentro la cambia desde su perfil',
  [RUTA_DE_RESTABLECIMIENTO]:
    'se abre desde el enlace del correo con su testigo en la cadena de consulta, así que no hay enlace que ofrecer',
};

/** Dónde está la persona dentro de la navegación: la pestaña y la vista. */
export interface PosicionEnLaNavegacion {
  readonly grupo: GrupoDeNavegacion;
  readonly vista: VistaDeNavegacion;
}

/**
 * El camino de la pestaña de un grupo: la primera de sus vistas.
 *
 * @param grupo grupo del que se quiere el destino
 * @returns el camino de su portada
 */
export function destinoDelGrupo(grupo: GrupoDeNavegacion): string {
  return grupo.vistas[0].camino;
}

/**
 * El camino tal como se compara: sin barra final y con la raíz garantizada.
 *
 * `/glicemias/` y `/glicemias` son la misma vista para quien escribe la dirección a mano, y sin este
 * paso la primera no marcaría ninguna pestaña. La cadena vacía vuelve como la raíz por lo mismo.
 *
 * @param camino el `pathname` de la ubicación actual
 */
function normalizado(camino: string): string {
  const conRaiz = camino.startsWith('/') ? camino : `/${camino}`;
  const sinBarraFinal = conRaiz.replace(/\/+$/u, '');

  return sinBarraFinal === '' ? RUTA_DE_INICIO : sinBarraFinal;
}

/**
 * Si una vista cubre una ubicación: la propia vista o algo que cuelga de ella.
 *
 * El descendiente cuenta porque una vista puede crecer con subcaminos —un detalle, un paso de un
 * asistente— y la pestaña tiene que seguir marcada mientras se está dentro. La raíz es la excepción
 * obligada: como prefijo cubriría toda la aplicación y dejaría Inicio siempre activa.
 */
function cubre(vista: string, ubicacion: string): boolean {
  if (ubicacion === vista) return true;

  return vista !== RUTA_DE_INICIO && ubicacion.startsWith(`${vista}/`);
}

/**
 * La pestaña y la vista que corresponden a una ubicación, o nada si la ubicación no está en la
 * navegación.
 *
 * **Gana el camino más largo, y de eso depende que las tres vistas bajo `/perfil` se marquen bien.**
 * `/perfil` es prefijo de `/perfil/sensor`, así que las dos lo cubren; con la primera coincidencia
 * ganando, el sensor marcaría la pestaña Perfil y el enlace equivocado. Con la más específica gana la
 * suya, que además está en otro grupo —Glucosa— por la razón que explica la cabecera.
 *
 * Devuelve `undefined` sin lanzar en tres casos que existen de verdad: las cuatro rutas del flujo de
 * autenticación, un camino inventado que cae en la vista de «no encontramos esa página», y cualquier
 * ruta futura que alguien agregue al enrutador sin sumarla a un grupo. En los tres, la barra se dibuja
 * completa y sin ninguna pestaña marcada, que es lo honesto: marcar una al azar diría que la persona
 * está en una sección en la que no está.
 *
 * @param camino el `pathname` de la ubicación actual, sin cadena de consulta
 */
export function posicionEnLaNavegacion(camino: string): PosicionEnLaNavegacion | undefined {
  const ubicacion = normalizado(camino);
  let mejor: PosicionEnLaNavegacion | undefined;

  for (const grupo of GRUPOS_DE_NAVEGACION) {
    for (const vista of grupo.vistas) {
      if (!cubre(vista.camino, ubicacion)) continue;
      if (mejor !== undefined && mejor.vista.camino.length >= vista.camino.length) continue;

      mejor = { grupo, vista };
    }
  }

  return mejor;
}
